import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VimeoVideo from 'src/components/VimeoVideo';
import {
  dismissPBTextingModal,
} from 'src/actions/accountActions';
import { setCurrentNavigationTab } from 'src/actions/accountSettingsActions';
import { ROUTES } from 'src/constants';
import pbTextingModalStyle from './pbTextingModal.style';

const TRANSITION_GUIDE_URL = 'https://docs.google.com/document/d/1rz-liSfcc8a6ehqqV4TmPTDyqTGKmwINdlTLQulPxlo';

function PBTextingModal({ classes }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  const { user, pbTextingModal } = useSelector((state) => state.account) || {};
  const {
    shouldShowModal,
    videos = [],
    isSunsetPeriod = false,
    subscriptionType,
    renewalDate,
    daysUntilRenewalFormatted,
    sunsetMessage,
    cancelAtPeriodEnd = false,
    supportEndDate,
    isAnnualAfterCutoff = false,
  } = pbTextingModal || {};
  const { firstName = 'there' } = user || {};

  const handleClose = useCallback(() => {
    dispatch(dismissPBTextingModal());
  }, [dispatch]);

  const handleGetStarted = useCallback(() => {
    dispatch(dismissPBTextingModal());
    dispatch(setCurrentNavigationTab('subscription'));
    history.push(ROUTES.APP_ACCOUNT);
  }, [dispatch, history]);

  const handleOpenGuide = useCallback(() => {
    window.open(TRANSITION_GUIDE_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const handleTabClick = useCallback((index) => {
    setSelectedVideoIndex(index);
  }, []);

  if (!shouldShowModal) {
    return null;
  }

  const selectedVideo = videos[selectedVideoIndex] || null;

  return (
    <Dialog
      open
      onClose={handleClose}
      aria-labelledby="pb-texting-modal-title"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
    >
      {/* 1. Purple header */}
      <div className={classes.headerBar}>
        <div className={classes.headerContent}>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <h2 id="pb-texting-modal-title" className={classes.headline}>
            Hey
            {' '}
            {firstName}
            !
            <br />
            Have you heard that CP now has texting built in?
          </h2>
        </div>
      </div>

      <div className={classes.content}>
        {/* Sunset Warning Section (shown when in sunset period) */}
        {isSunsetPeriod && (
          <div className={classes.sunsetWarning}>
            <div className={classes.sunsetWarningHeader}>
              <WarningAmberIcon className={classes.sunsetWarningIcon} />
              <Typography className={classes.sunsetWarningTitle}>
                Important: Connection Point is ending support for Project Broadcast
                texting for you on
                {' '}
                {supportEndDate || 'April 1, 2026'}
              </Typography>
            </div>
            {renewalDate && daysUntilRenewalFormatted && (
              <Typography className={classes.sunsetRenewalInfo}>
                Your
                {' '}
                {subscriptionType}
                {' '}
                subscription
                {' '}
                {/* eslint-disable-next-line no-nested-ternary */}
                {cancelAtPeriodEnd
                  ? 'is set to end on'
                  : (isAnnualAfterCutoff ? 'would have renewed on' : 'currently renews on')}
                {' '}
                <strong>{renewalDate}</strong>
                {' '}
                - in
                {' '}
                {daysUntilRenewalFormatted}
              </Typography>
            )}
            {sunsetMessage && !cancelAtPeriodEnd && (
              <Typography className={classes.sunsetMessage}>
                {sunsetMessage}
              </Typography>
            )}
            <Typography className={classes.sunsetCta}>
              To continue using Connection Point after that,
              {' '}
              <button
                type="button"
                className={classes.sunsetCtaLink}
                onClick={handleGetStarted}
              >
                subscribe to a CP Texting plan
              </button>
              .
            </Typography>
          </div>
        )}

        {/* 2. Intro paragraph */}
        <div className={classes.introText}>
          <p style={{ margin: 0 }}>
            Connection Point now includes
            {' '}
            <strong>built-in texting</strong>
            {' '}
            powered by our own infrastructure.
          </p>
          <p style={{ margin: '8px 0 0 0' }}>
            This means more powerful features for your business,
            simplified billing, and all your tools in one place.
          </p>
          <p style={{ margin: '12px 0 0 0', fontStyle: 'italic' }}>
            Many have already made the switch and are finding better deliverability as well!
          </p>
        </div>

        {/* 3. Checklist */}
        <div className={classes.benefitsList}>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon}>✓</span>
            <span>One integrated system!</span>
          </div>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon}>✓</span>
            <span>No running out of credits nor pre-buying them!</span>
          </div>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon}>✓</span>
            <span>Simplified billing - one subscription covers everything</span>
          </div>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon}>✓</span>
            <span>Far Smarter with Texting Built-In - for example CP Groups that work alongside Events!</span>
          </div>
        </div>

        {/* 4. Light purple CTA box */}
        <div className={classes.ctaCard}>
          <Typography className={classes.ctaLine1}>
            Click below to go to the Subscription tab and add CP Texting.
          </Typography>
          <Typography className={classes.ctaLine2}>
            If you&apos;re mid-subscription, you&apos;ll receive credit for the remaining time.
          </Typography>
        </div>

        {/* 5. Get Started button */}
        <Button
          className={classes.primaryButton}
          onClick={handleGetStarted}
          fullWidth
          variant="contained"
          disableElevation
        >
          Get Started with CP Texting
        </Button>

        {/* 6. Transition Guide link */}
        <div className={classes.secondaryLinkWrapper}>
          <button
            type="button"
            className={classes.secondaryLink}
            onClick={handleOpenGuide}
          >
            Read the Transition Guide
            <OpenInNewIcon className={classes.externalIcon} />
          </button>
        </div>

        {/* 7. Video section (conditional) */}
        {videos.length > 0 && (
          <div className={classes.videoSection}>
            {/* Tabs if 2+ videos */}
            {videos.length >= 2 && (
              <div className={classes.videoTabs}>
                {videos.map((video, index) => (
                  <button
                    key={video.url}
                    type="button"
                    className={`${classes.videoTab} ${index === selectedVideoIndex ? classes.videoTabActive : ''}`}
                    onClick={() => handleTabClick(index)}
                  >
                    {video.title}
                  </button>
                ))}
              </div>
            )}

            {/* Description (italic) */}
            {selectedVideo?.description && (
              <Typography className={classes.videoDescription}>
                {selectedVideo.description}
              </Typography>
            )}

            {/* Video player */}
            <div className={classes.videoWrapper}>
              <div className={classes.videoContainer}>
                <VimeoVideo
                  link={selectedVideo?.url}
                  title={selectedVideo?.title || 'CP Texting Introduction'}
                  width="100%"
                  height="100%"
                  positionsY="center"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 8. Not Now button */}
      <div className={classes.footer}>
        <button
          type="button"
          className={classes.notNowButton}
          onClick={handleClose}
        >
          Not Now
        </button>
      </div>
    </Dialog>
  );
}

PBTextingModal.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(PBTextingModal, pbTextingModalStyle);
